import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ClipboardList, X, Trash2, ExternalLink, CheckCircle2, ArrowUpToLine, ArrowRightLeft, Search, PlusCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminPendingDividendReviewButton — May 2026.
 *
 * Admin-only button that opens a modal listing tickers seen via
 * `/api/stocks/{ticker}/preview` that yfinance reports as
 * dividend-paying but are NOT in our hard-frozen 4,595-ticker
 * dividend cache. These are CANDIDATES that the admin may want to
 * manually audit and promote later — we never auto-promote.
 *
 * The list is sorted by view_count desc (most-clicked floats to the
 * top) so the admin can prioritise.
 */
export default function AdminPendingDividendReviewButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  // Round 94e (Feb 2026) — Search field + bulk-promote.
  // `selected` is the set of tickers the user has checked; `searchQuery`
  // is the live filter. The bulk-add button runs through the SELECTED
  // tickers in alphabetical order, surfacing a toast per ticker and
  // moving on to the next on failure (matches the user's spec).
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkAdding, setBulkAdding] = useState(false);
  // Confirm-prompt state — replaces window.confirm() which silently
  // fails in some embedded / iframe contexts.
  const [confirmAction, setConfirmAction] = useState(null);  // {kind:'promote'|'dismiss', candidate}
  // Round P113 (Feb 2026) — Manual onboard. Admin can type any ticker
  // that never surfaced in the auto-discovery pipeline (e.g. VOO on
  // a fresh production DB) and force-promote it via the same
  // `/promote` endpoint — that endpoint already does the right thing
  // for arbitrary tickers (409 if already tracked, 400 if Massive has
  // no dividend history).
  const [manualTicker, setManualTicker] = useState('');
  const [manualAdding, setManualAdding] = useState(false);

  // Round 94e — search-filtered + always alphabetically sorted view of
  // the candidate pool. The backend's natural order is view-count-desc;
  // we override here so the bulk-add loop's "alphabetical" order
  // matches what the admin SEES on screen as they tick boxes.
  // NOTE: useMemo must run on every render (it can't sit after the
  // `user?.role` early-return below), per the React Rules of Hooks.
  const visibleCandidates = useMemo(() => {
    const all = report?.candidates || [];
    const q = searchQuery.trim().toUpperCase();
    const filtered = q ? all.filter(c => (c.ticker || '').toUpperCase().includes(q)) : all;
    return [...filtered].sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
  }, [report, searchQuery]);

  if (user?.role !== 'admin') return null;

  const fetchList = async () => {
    setLoading(true);
    setReport(null);
    setOpen(true);
    try {
      const { data } = await axios.get(`${API}/admin/pending-dividend-review`, { withCredentials: true });
      setReport(data);
    } catch (err) {
      toast.error('Could not load pending review list', {
        description: err?.response?.data?.detail || err?.message,
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const dismiss = async (ticker) => {
    try {
      await axios.delete(`${API}/admin/pending-dividend-review/${encodeURIComponent(ticker)}`, { withCredentials: true });
      setReport(prev => ({ ...prev, candidates: (prev?.candidates || []).filter(c => c.ticker !== ticker), count: (prev?.count || 1) - 1 }));
      toast.success(`Dismissed ${ticker}`);
    } catch (err) {
      toast.error(`Could not dismiss ${ticker}`, { description: err?.message });
    }
  };

  const moveToNonDividend = async (ticker) => {
    setBusyTicker(ticker);
    try {
      await axios.post(
        `${API}/admin/pending-dividend-review/${encodeURIComponent(ticker)}/move-to-non-dividend`,
        {},
        { withCredentials: true }
      );
      setReport(prev => ({ ...prev, candidates: (prev?.candidates || []).filter(c => c.ticker !== ticker), count: Math.max(0, (prev?.count || 1) - 1) }));
      toast.success(`Moved ${ticker} → Pending Non-Dividend Review`);
    } catch (err) {
      toast.error(`Could not move ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const promote = async (ticker) => {
    setBusyTicker(ticker);
    try {
      const { data } = await axios.post(
        `${API}/admin/pending-dividend-review/${encodeURIComponent(ticker)}/promote`,
        {},
        { withCredentials: true }
      );
      // Round 98r (Jul 2026) — endpoint now returns 200 with
      // `{ok:false, detail}` for Cloudflare-stripped 5xx cases
      // (missing API key, Massive fetch failed). Surface as error
      // toast and leave the row in the queue so admin can retry.
      if (data && data.ok === false) {
        toast.error(`Could not promote ${ticker}`, {
          description: data.detail || 'Upstream failure — see server logs',
          duration: 8000,
        });
        return;
      }
      setReport(prev => ({ ...prev, candidates: (prev?.candidates || []).filter(c => c.ticker !== ticker), count: (prev?.count || 1) - 1 }));
      toast.success(`Promoted ${ticker}`, {
        description: `${data.name} · ${data.dividend_yield}% yield · $${data.dividend_rate}/yr · ${data.frequency}`,
        duration: 6000,
      });
    } catch (err) {
      toast.error(`Could not promote ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
        duration: 8000,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Manual onboard — promote an arbitrary ticker the auto-discovery
  // cron never surfaced. Uses the same `/promote` endpoint; that
  // endpoint returns 409 if the ticker is already in stock_cache and
  // 400 if Massive has no dividend history for it.
  const manualOnboard = async () => {
    const t = (manualTicker || '').trim().toUpperCase();
    if (!t) {
      toast.error('Enter a ticker symbol first');
      return;
    }
    if (!/^[A-Z][A-Z.\-]{0,9}$/.test(t)) {
      toast.error(`"${t}" doesn't look like a valid ticker`);
      return;
    }
    setManualAdding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/pending-dividend-review/${encodeURIComponent(t)}/promote`,
        {},
        { withCredentials: true }
      );
      if (data && data.ok === false) {
        toast.error(`Could not add ${t}`, {
          description: data.detail || 'Upstream failure — see server logs',
          duration: 8000,
        });
        return;
      }
      const wasMove = !!data.moved_from_non_div;
      toast.success(wasMove ? `Moved ${t} to dividend DB` : `Added ${t}`, {
        description: `${data.name || ''} · ${data.dividend_yield}% · $${data.dividend_rate}/yr · ${data.frequency || ''}`,
        duration: 6000,
      });
      setManualTicker('');
      // In case the ticker also happened to be in the pending queue,
      // drop it from the local list so the row disappears immediately.
      setReport(prev => prev ? {
        ...prev,
        candidates: (prev.candidates || []).filter(c => c.ticker !== t),
        count: Math.max(0, (prev.count || 1) - 1),
      } : prev);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 409) {
        toast.error(`${t} is already in the dividend DB`, { duration: 6000 });
      } else if (status === 400) {
        toast.error(`Cannot add ${t}`, {
          description: detail || 'Massive has no dividend history for this ticker.',
          duration: 8000,
        });
      } else {
        toast.error(`Could not add ${t}`, { description: detail, duration: 8000 });
      }
    } finally {
      setManualAdding(false);
    }
  };

  // Run whichever action the confirmation modal returned, then close it.
  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { kind, candidate } = confirmAction;
    setConfirmAction(null);
    if (kind === 'promote') await promote(candidate.ticker);
    else if (kind === 'dismiss') await dismiss(candidate.ticker);
  };

  // Master checkbox state — derived. "Checked" iff every visible row is
  // already selected. "Indeterminate" handled via the `:indeterminate`
  // pseudo-class on the input element so we don't have to fight React
  // controlled-checkbox semantics for the mixed state.
  const allVisibleSelected = visibleCandidates.length > 0 &&
    visibleCandidates.every(c => selected.has(c.ticker));

  const toggleOne = (ticker) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker); else next.add(ticker);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Uncheck every currently-visible row (preserves selections on
        // tickers hidden by the search filter — feels less surprising).
        visibleCandidates.forEach(c => next.delete(c.ticker));
      } else {
        visibleCandidates.forEach(c => next.add(c.ticker));
      }
      return next;
    });
  };

  // Bulk promote — iterates over checked tickers in alphabetical order,
  // calls the existing single-ticker promote endpoint one at a time
  // (serial, not parallel — the endpoint runs the 5-field dividend
  // gate + name lookup which is heavy and we don't want to hammer
  // Massive/Yahoo with 50 concurrent calls). On each failure shows a
  // toast and moves on to the next, exactly per the user's spec.
  const bulkPromote = async () => {
    const tickers = [...selected].sort();
    if (tickers.length === 0) {
      toast.error('No tickers selected');
      return;
    }
    setBulkAdding(true);
    const tally = { ok: 0, fail: 0 };
    for (const tk of tickers) {
      setBusyTicker(tk);
      try {
        const { data } = await axios.post(
          `${API}/admin/pending-dividend-review/${encodeURIComponent(tk)}/promote`,
          {},
          { withCredentials: true }
        );
        tally.ok += 1;
        toast.success(`Added ${tk}`, {
          description: `${data.name || ''} · ${data.dividend_yield}% · $${data.dividend_rate}/yr · ${data.frequency || ''}`,
          duration: 3500,
        });
        setReport(prev => ({
          ...prev,
          candidates: (prev?.candidates || []).filter(c => c.ticker !== tk),
          count: Math.max(0, (prev?.count || 1) - 1),
        }));
        setSelected(prev => { const n = new Set(prev); n.delete(tk); return n; });
      } catch (err) {
        tally.fail += 1;
        toast.error(`Skipped ${tk}`, {
          description: err?.response?.data?.detail || err?.message || 'Promotion failed',
          duration: 4500,
        });
      }
    }
    setBusyTicker(null);
    setBulkAdding(false);
    toast.success(`Bulk add complete — ${tally.ok} added, ${tally.fail} skipped`, { duration: 5000 });
  };

  return (
    <>
      <button
        type="button"
        onClick={fetchList}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: tickers users have viewed via /preview that Massive API reports as dividend-paying (recurring rows in the last 365 days) but aren't in our hard-frozen DB. Audit candidates here, then promote manually when data quality is acceptable."
        data-testid="admin-pending-dividend-review-fire"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardList className="w-3 h-3" />}
        Pending dividend review
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="admin-pending-review-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <ClipboardList className="w-4 h-4 text-violet-600" />
              <div className="flex-1">
                <div className="text-sm font-bold">Pending dividend review</div>
                <div className="text-[10px] text-muted-foreground">
                  Off-database tickers seen via /preview that Massive API reports as dividend-paying.
                  Sorted by view-count.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-pending-review-close"
                aria-label="Close pending review modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {/* Round P113 — Manual onboard input.
                  Always visible whether loading or not; lets the admin
                  add popular tickers like VOO/SPY that never surfaced
                  through the auto-discovery cron on a fresh DB. */}
              <div className="rounded-md border border-violet-500/40 bg-violet-500/5 p-2.5" data-testid="pending-review-manual-add">
                <div className="flex items-center gap-2 mb-1.5">
                  <PlusCircle className="w-3.5 h-3.5 text-violet-600 dark:text-violet-300" />
                  <div className="text-[11px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-200">Manual add</div>
                  <div className="text-[10px] text-muted-foreground italic ml-auto hidden sm:block">
                    For popular tickers the auto-discovery cron never surfaced (e.g. VOO on a fresh DB).
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={manualTicker}
                    onChange={(e) => setManualTicker(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !manualAdding) manualOnboard(); }}
                    placeholder="Type ticker (e.g. VOO)…"
                    className="flex-1 px-2.5 py-1.5 text-xs rounded-md border border-border bg-background font-mono uppercase tracking-wider focus:outline-none focus:ring-1 focus:ring-violet-500"
                    data-testid="pending-review-manual-input"
                    disabled={manualAdding}
                    maxLength={10}
                  />
                  <button
                    type="button"
                    onClick={manualOnboard}
                    disabled={manualAdding || !manualTicker.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Fetch this ticker from Massive and add to the main dividend DB. Fails cleanly if the ticker isn't a dividend payer or is already tracked."
                    data-testid="pending-review-manual-add-btn"
                  >
                    {manualAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3" />}
                    Add ticker
                  </button>
                </div>
              </div>

              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              )}

              {report && (
                <>
                  <div className="text-[11px] text-muted-foreground" data-testid="pending-review-count">
                    {report.count} candidate{report.count === 1 ? '' : 's'} found.
                  </div>

                  {report.count === 0 && (
                    <div className="flex items-center gap-2 px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      No candidates right now. The list grows as users browse off-database
                      tickers that Massive API reports as dividend-paying.
                    </div>
                  )}

                  {report.candidates && report.candidates.length > 0 && (
                    <>
                      {/* Round 94e — Search + bulk-add toolbar */}
                      <div className="flex items-center gap-2 mb-2" data-testid="pending-review-toolbar">
                        <div className="relative flex-1">
                          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search ticker…"
                            className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-violet-500"
                            data-testid="pending-review-search"
                            disabled={bulkAdding}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={bulkPromote}
                          disabled={bulkAdding || selected.size === 0}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          title="Promote every checked ticker, in alphabetical order. Failures are toasted and skipped."
                          data-testid="pending-review-bulk-add"
                        >
                          {bulkAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUpToLine className="w-3 h-3" />}
                          Add Checked ({selected.size})
                        </button>
                      </div>

                      {visibleCandidates.length === 0 ? (
                        <div className="text-[11px] text-muted-foreground italic py-3 text-center" data-testid="pending-review-no-match">
                          No tickers match &quot;{searchQuery}&quot;.
                        </div>
                      ) : (
                        <div className="border border-border/60 rounded-md overflow-hidden">
                          <div className="grid grid-cols-[28px_60px_1fr_70px_60px_60px_60px_30px_30px_30px] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-bold uppercase tracking-wide text-muted-foreground items-center">
                            <div>
                              {/* Master checkbox — selects every currently
                                  visible row. Indeterminate state shown when
                                  at least one but not all are checked. */}
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                ref={(el) => {
                                  if (el) {
                                    const someChecked = visibleCandidates.some(c => selected.has(c.ticker));
                                    el.indeterminate = !allVisibleSelected && someChecked;
                                  }
                                }}
                                onChange={toggleAllVisible}
                                className="w-3.5 h-3.5 accent-violet-600 cursor-pointer"
                                data-testid="pending-review-master-checkbox"
                                title={allVisibleSelected ? 'Uncheck all visible' : 'Check all visible'}
                                disabled={bulkAdding}
                              />
                            </div>
                            <div>Ticker</div>
                            <div>Name</div>
                            <div className="text-right">Yield %</div>
                            <div className="text-right">Rate $</div>
                            <div className="text-right">Freq</div>
                            <div className="text-right">Views</div>
                            <div title="Promote into main DB">Add</div>
                            <div title="Move to Pending Non-Dividend Review" className="text-center">Non</div>
                            <div />
                          </div>
                          <div className="divide-y divide-border/40">
                            {visibleCandidates.map((c) => (
                              <div
                                key={c.ticker}
                                className="grid grid-cols-[28px_60px_1fr_70px_70px_60px_60px_30px_30px_30px] gap-2 px-3 py-1.5 items-center text-xs hover:bg-muted/30"
                                data-testid={`pending-review-row-${c.ticker}`}
                              >
                                <div>
                                  <input
                                    type="checkbox"
                                    checked={selected.has(c.ticker)}
                                    onChange={() => toggleOne(c.ticker)}
                                    className="w-3.5 h-3.5 accent-violet-600 cursor-pointer"
                                    data-testid={`pending-review-checkbox-${c.ticker}`}
                                    disabled={bulkAdding}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => onStockClick?.(c.ticker, null, { previewOnly: true })}
                                  className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline inline-flex items-center gap-0.5"
                                  title={`Preview ${c.ticker} in Stock Modal (no persist, no auto-promote)`}
                                >
                                  {c.ticker} <ExternalLink className="w-2.5 h-2.5" />
                                </button>
                                <div className="truncate text-muted-foreground" title={c.name}>
                                  {c.name || '—'}
                                </div>
                                <div className="text-right font-mono">
                                  {c.yfinance_dividend_yield != null ? c.yfinance_dividend_yield.toFixed(2) : '—'}
                                </div>
                                <div
                                  className={`text-right font-mono ${c.rate_source === 'massive_ttm' ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : ''}`}
                                  title={c.rate_source === 'massive_ttm'
                                    ? `Massive TTM (trailing 12 months): $${(c.rate_ttm || 0).toFixed(4)}/share/yr`
                                    : 'Yahoo-reported rate (Massive had no data — promotion will refuse).'}
                                >
                                  {c.rate_ttm != null && c.rate_ttm > 0
                                    ? c.rate_ttm.toFixed(2)
                                    : (c.yfinance_dividend_rate != null && c.yfinance_dividend_rate > 0
                                        ? c.yfinance_dividend_rate.toFixed(2)
                                        : '—')}
                                </div>
                                <div className="text-right text-[10px] text-muted-foreground">
                                  {c.yfinance_frequency || '—'}
                                </div>
                                <div className="text-right font-mono font-bold">{c.view_count || 0}</div>
                                <button
                                  type="button"
                                  onClick={() => setConfirmAction({ kind: 'promote', candidate: c })}
                                  disabled={busyTicker === c.ticker || bulkAdding}
                                  className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
                                  title={`Promote ${c.ticker} to main dividend DB (enriches via Massive + Yahoo, locks the name, runs the 5-field gate)`}
                                  data-testid={`pending-review-promote-${c.ticker}`}
                                >
                                  {busyTicker === c.ticker ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUpToLine className="w-3 h-3" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveToNonDividend(c.ticker)}
                                  disabled={busyTicker === c.ticker || bulkAdding}
                                  className="text-sky-600 hover:text-sky-700 disabled:opacity-40 flex items-center justify-center"
                                  title={`Move ${c.ticker} to the Pending Non-Dividend Review queue (if it doesn't belong in the dividend DB)`}
                                  data-testid={`pending-review-move-non-div-${c.ticker}`}
                                >
                                  {busyTicker === c.ticker ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightLeft className="w-3 h-3" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmAction({ kind: 'dismiss', candidate: c })}
                                  disabled={busyTicker === c.ticker || bulkAdding}
                                  className="text-muted-foreground hover:text-rose-600 disabled:opacity-40"
                                  title={`Drop ${c.ticker} from this list`}
                                  data-testid={`pending-review-dismiss-${c.ticker}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/60 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-muted"
                data-testid="admin-pending-review-dismiss-modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation modal — replaces window.confirm() which can be
          silently blocked in iframe / embedded contexts. Shows the
          actual data we'll write so the admin can sanity-check before
          committing. */}
      {confirmAction && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmAction(null)}
          data-testid="pending-review-confirm-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setConfirmAction(null)}
              className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground rounded p-1"
              aria-label="Close"
              data-testid="pending-review-confirm-close-x"
            >
              <X className="w-4 h-4" />
            </button>

            {confirmAction.kind === 'promote' ? (
              <>
                <div className="px-5 pt-5 pb-2 flex items-center gap-3">
                  <ArrowUpToLine className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="text-sm font-bold">
                    Promote {confirmAction.candidate.ticker} into the main dividend DB?
                  </div>
                </div>
                <div className="px-5 pb-2 text-[11px] text-muted-foreground space-y-1">
                  <div><b className="text-foreground">{confirmAction.candidate.name || confirmAction.candidate.ticker}</b></div>
                  <div>
                    Massive TTM rate:{' '}
                    <span className="font-mono text-emerald-700 dark:text-emerald-400">
                      ${(confirmAction.candidate.rate_ttm || confirmAction.candidate.yfinance_dividend_rate || 0).toFixed(2)}/share/yr
                    </span>
                    {' · '}
                    Yield: {(confirmAction.candidate.yfinance_dividend_yield || 0).toFixed(2)}%
                  </div>
                  <ul className="list-disc ml-4 pt-2 space-y-0.5">
                    <li>Pulls full dividend history + ex/pay dates from Massive.</li>
                    <li>Pulls market_cap / p/e / beta / etc. from Yahoo.</li>
                    <li>Locks the longest available company name.</li>
                    <li>Stamps dividend dates as <span className="font-mono">massive</span>-owned (Yahoo can&apos;t overwrite).</li>
                    <li>Bypasses the hard-freeze rule for this one ticker.</li>
                  </ul>
                  <div className="pt-1">
                    The 5-field gate (yield, rate, frequency, ex-date, pay-date) must pass.
                    If any field is missing, promotion will refuse and explain why.
                  </div>
                </div>
                <div className="px-5 pb-5 pt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmAction(null)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted"
                    data-testid="pending-review-confirm-no"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="pending-review-confirm-yes"
                  >
                    <ArrowUpToLine className="w-3 h-3" />
                    Yes, promote
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-5 pt-5 pb-2 flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-rose-600 shrink-0" />
                  <div className="text-sm font-bold">
                    Drop {confirmAction.candidate.ticker} from this list?
                  </div>
                </div>
                <div className="px-5 pb-2 text-[11px] text-muted-foreground">
                  {confirmAction.candidate.name || ''} will be re-added the next time someone views it via /preview if yfinance still reports dividends.
                </div>
                <div className="px-5 pb-5 pt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmAction(null)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted"
                    data-testid="pending-review-confirm-no"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="pending-review-confirm-yes"
                  >
                    <Trash2 className="w-3 h-3" />
                    Yes, drop
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
